import { useState, useEffect, FC } from 'react'
import { NavLink, Prompt, Redirect, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Formik, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import { Capacitor } from '@capacitor/core'
import { useTranslation } from 'react-i18next'
import { AxiosError } from 'axios'

import { Auth } from 'services/Api'
import { Card as MerinioCard } from 'components/UI'
import { Button, Input, Form } from 'components/Forms'
import { Alert, Card } from 'ui'
import { currentLang } from 'translations'

type InitialValues = {
  password: string
  confirmPassword: string
}

const Reset: FC = () => {
  const { t } = useTranslation('legacy')
  const { co } = useSelector((state: MStore.RootState) => state.auth)

  const [validationParams, setValidationParams] = useState<Record<string, RegExp>>({})

  const [error, setError] = useState<string | null>(null)
  const [redirectToLogin, setRedirectToLogin] = useState(false)
  const { token } = useParams<{ token: string }>()

  useEffect(() => {
    async function validateToken() {
      const { message, success, ...rest } = await Auth(`/reset/${token}?co=${co}`)
      if (!success) return setError(message)
      setValidationParams(rest)
    }
    validateToken()
  }, [co, token])

  const validation = !validationParams?.customPasswordValidation
    ? [new RegExp('^.{8,64}$'), 'employee.myProfile.PASSWORD_REQUIREMENT']
    : [new RegExp(validationParams?.passwordValidationRegex), validationParams.passwordValidationMsg]

  // Form validation
  const EditPasswordShape = Yup.object().shape({
    password: Yup.string()
      .matches(...(validation as [RegExp, string]))
      .required('employee.myProfile.MANDATORY_PASSWORD'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], 'employee.myProfile.CONFIRMATION_FIELD_MUST_MATCH')
      .required('employee.myProfile.MANDATORY_CONFIRM_PASSWORD')
  })

  const onSubmit = async (values: InitialValues, actions: FormikHelpers<InitialValues>) => {
    const { password, confirmPassword } = values
    try {
      const res = await Auth.post(`/reset/${token}`, { co, confirmPassword, password, token, lang: currentLang() })
      if (!res.success) {
        actions.setSubmitting(false)
        // actions.setErrors(error)
        actions.setStatus({ msg: res.message })
      } else {
        actions.setSubmitting(false)
        actions.resetForm({ values: { password: '', confirmPassword: '' } })
        setRedirectToLogin(true)
      }
    } catch (err) {
      const error = err as AxiosError

      actions.setSubmitting(false)
      actions.setStatus({ msg: error.message })
    }
  }

  // If no company was found, redirect the user to a page to select one
  if (!co) return <Redirect to="/company" />

  // Redirect to the login page
  if (redirectToLogin) return <Redirect to="/login" />

  const initialValues: InitialValues = {
    password: '',
    confirmPassword: ''
  }

  // Return the login form
  return (
    <MerinioCard>
      <Card.Body>
        <Formik validationSchema={EditPasswordShape} initialValues={initialValues} onSubmit={onSubmit}>
          {({ status, isSubmitting, dirty, setStatus }) => (
            <Form dirty={!!dirty && !isSubmitting}>
              <Alert onClose={() => setStatus()} show={!!status?.msg}>
                {status?.msg}
              </Alert>
              <Alert onClose={() => setError(null)} show={!!error}>
                {t(error)}
              </Alert>
              <Input
                type="password"
                name="password"
                module="onboarding"
                autoComplete="new-password"
                required
                autoCorrect="off"
                autoCapitalize="none"
                disabled={!!error}
                customDescription={validationParams?.customPasswordValidation ? validationParams.passwordValidationMsg : null}
              />
              <Input
                type="password"
                name="confirmPassword"
                module="onboarding"
                autoComplete="new-password"
                required
                autoCorrect="off"
                autoCapitalize="none"
                disabled={!!error}
              />
              <div className="mb-4 d-flex">
                <Button type="submit" isSubmitting={isSubmitting} text="auth.SUBMIT" disabled={!!error} />
                <div className="ml-auto">
                  <div>
                    {!Capacitor.isNativePlatform() ? (
                      <NavLink to="/forgot/username">{t('auth.FORGOT_USERNAME')}</NavLink>
                    ) : (
                      <a href={`https://${co}.merinio.com/forget/username`}>{t('auth.FORGOT_USERNAME')}</a>
                    )}
                  </div>
                  <div>
                    {!Capacitor.isNativePlatform() ? (
                      <NavLink to="/forgot/password">{t('auth.FORGOT_PASSWORD')}</NavLink>
                    ) : (
                      <a href={`https://${co}.merinio.com/forget/password`}>{t('auth.FORGOT_PASSWORD')}</a>
                    )}
                  </div>
                </div>
              </div>
              <Prompt when={dirty} message="Are you sure you want to leave?" />
            </Form>
          )}
        </Formik>
      </Card.Body>
    </MerinioCard>
  )
}

export default Reset
